<template>
    <div class="widget-base-content-wrapper" :class="[variant, wrapperSize, extraClass, {'mobile': getMobileAppState}]">
        <div v-if="variant !== 'full-widget'" class="widget-header" :class="{'with-shadow': applyShadowToHeader}">
            <div v-if="!tabsList" class="title">{{title}}</div>
            <tab-generic v-else :tabsList="tabsList" @clicked="emit('tabClicked', $event)"/>
            <button-quaternary v-if="!hideRedirectIcon" icon="arrow-right-full" @clicked="emit('redirectToFeature')"/>
        </div>
        <div class="widget-content">
            <empty-state-error v-if="applyErrorState" :message="t('home.errorMessage')"/>
            <slot v-else></slot>
            <!-- UnderDevelopment -->
            <empty-state-generic
            v-if="underDevelopment"
            illustration="emptyState/available-soon"
            :title="t('underDevelopment.title')"
            :description="t('underDevelopment.description')"/>
        </div>
    </div>
    <!-- Token Modal -->
    <modal-centered-token
    v-if="getShowTokenModalDisplayState && hasTokenModal"
    :titleAppend="tokenTitleAppend"
    :titleFull="tokenTitleFull"
    :params="tokenParams"
    @hideMe="[hideTokenChallengeModal(), emit('hideTokenChallengeModal')]"
    @challengeSuccessHandler="emit('challengeSuccessHandler', $event)"
    @challengeErrorHandler="emit('challengeErrorHandler', $event)"/>
</template>

<script setup>
import TabGeneric from '@/components/generic/tabs/Generic'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ModalCenteredToken from '@/components/generic/modals/centered/Token'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['tabClicked', 'redirectToFeature', 'hideTokenChallengeModal', 'challengeSuccessHandler', 'challengeErrorHandler'])

/* Props
@applyShadowToHeader {Boolean} prop to apply shadow at the bottom of our header
@extraClass {String} - can be [ none (default), cards, merchant]
@variant {String} - can be [ none (default), anchor]
@wrapperSize {String} - can be [ none (default), big]
@title {String}
@tabsList {Array}
@applyErrorState {Boolean}
@underDevelopment {Boolean}
@hideRedirectIcon {Boolean} prop to hide redirectButton
@hasTokenModal {Boolean} - prop to indicate if our widget has and should display token modal
@tokenTitleAppend {String} - string to appentd at token modal title
@tokenTitleFull {String} - string to use as token modal title
@tokenParams {Object} - some times token submit will have additional params to send with token, when so, send it in this prop
*/
const props = defineProps({
    applyShadowToHeader: Boolean,
    extraClass: String,
    variant: String,
    wrapperSize: String,
	title: String,
	tabsList: Array,
    applyErrorState: Boolean,
    underDevelopment: Boolean,
    hideRedirectIcon: Boolean,
    /* Token related Props */
    hasTokenModal: Boolean,
    tokenTitleAppend: String,
    tokenTitleFull: String,
    tokenParams: Object
})

/* mapGetters */
const { hideTokenChallengeModal } = mapActions()
const { getMobileAppState, getShowTokenModalDisplayState } = mapGetters()
</script>

<style lang="scss" scoped>
.widget-base-content-wrapper {
    width: 100%;
    height: 336px;

    border-radius: 16px;
    background-color: var(--secondary-light-4);
    overflow: hidden;
    z-index: 1;

    &.visible-overflow, &.exchanges {
        overflow: visible;
        z-index: 2;
    }

    &.anchor {
        height: 400px;
        background-color: var(--secondary-light-3);
    }

    &.tpas {
        background-color: var(--dashboard-05);

        .widget-header {
            &:after {
                background-color: var(--secondary);
            }
        }
    }

    &.operators {
        background-color: var(--dashboard-06);

        .widget-header {
            &:after {
                background-color: var(--secondary);
            }
        }
    }

    &.merchant {
        background-color: var(--dashboard-07);

        .widget-header {
            &:after {
                background-color: var(--secondary);
            }
        }
    }

    &.big {
        width: 638px;
        max-width: unset;
        flex-shrink: 0;
    }

    .widget-header {
        width: 100%;
        height: 40px;
        padding: 0 16px;

        display: flex;
        align-items: center;
        position: relative;

        transform: translateZ(0);
        z-index: 1;

        &:after {
            content: '';
            width: calc(100% - 32px);
            height: 1px;

            position: absolute;
            bottom: 0;
            left: 16px;

            background-color: var(--secondary-light-2);
        }

        &.with-shadow {
            box-shadow: var(--box-shadow-1-inverted);
        }

        .title {
            width: 100%;

            @include H7-Medium;
            color: var(--primary);
        }

        .button-quaternary {
            flex-shrink: 0;
        }
    }

    .widget-content {
        width: 100%;
        height: calc(100% - 40px);
        padding: 0 0 16px 0;

        .error-state-wrapper {
            height: 100%;

            :deep(.message) {
                max-width: 200px;
                text-align: center;
            }
        }

        :deep(.empty-state-wrapper) {
            height: 100%;
            background-color: transparent;

            svg {
                width: 80px;
                height: 80px;
            }
        }
    }

    &.full-widget {
        .widget-content {
            height: 100%;
        }
    }

    &.balances-and-movements {
        .widget-content {
            padding-top: 0;
            position: relative;
        }
    }

    &.naHora {
        position: relative;
    }

    &.mobile {
        height: auto;

        &.big {
            width: 100%;
        }

        &.cards {
            height: 336px;

            .widget-content {
                padding: 0 0 24px 0;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
            }
        }

        &.naHora {
            :deep(.spinner-wrapper) {
                min-height: 400px;
            }
        }
    }
}
</style>
